import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Heart, Star, Crown, Flame, Sparkles, Play, Lock, ChevronRight, Dumbbell, Palmtree, Users, Beef, Waves, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Icon mapping for dynamic rendering
const ICON_MAP = {
  Dumbbell: Dumbbell,
  Palmtree: Palmtree,
  Users: Users,
  Beef: Beef,
  Waves: Waves,
  Sparkles: Sparkles,
  Flame: Flame,
  Crown: Crown,
  Heart: Heart,
  Star: Star
};

// Fallback models if API fails
const FALLBACK_MODELS = [
  {
    id: 'cash',
    name: 'Cash',
    type: 'Fitness Model',
    tagline: 'Gym Enthusiast • Your Fantasy',
    icon: 'Dumbbell',
    gradient: 'from-orange-500 to-red-500',
    images: [
      'https://customer-assets.emergentagent.com/job_tradie-leads-1/artifacts/otfad551_2026-03-22_22-12-02_5674.png',
    ],
    content: [
      { id: 1, title: 'Morning Workout', type: 'Gym Session' },
    ],
  }
];

// Subscription Tiers
const TIERS = [
  {
    name: 'Sweet',
    price: '$12.99',
    period: '/month',
    icon: Heart,
    color: 'from-pink-500 to-rose-500',
    features: ['Access to all models', 'Weekly new content', 'Basic chat access', 'Mobile friendly'],
  },
  {
    name: 'Spicy',
    price: '$18.99',
    period: '/month',
    icon: Flame,
    color: 'from-orange-500 to-red-500',
    features: ['Everything in Sweet', 'Exclusive photoshoots', 'Priority chat responses', 'Behind the scenes'],
    popular: true,
  },
  {
    name: 'Red Hot Hunks',
    price: '$24.99',
    originalPrice: '$49.99',
    period: '/month',
    icon: Crown,
    color: 'from-amber-400 to-orange-600',
    features: ['Everything in Spicy', 'Custom content requests', 'Live session access', 'VIP Discord access', '1-on-1 experiences'],
    launch: true,
  },
];

const MyBoyLollipop = () => {
  const [showPromo, setShowPromo] = useState(true);
  const [dfansLink, setDfansLink] = useState('https://dfans.co/myboylollipop');
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // Fetch models from API
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const res = await fetch(`${API_URL}/api/lollipop/models`);
        const data = await res.json();
        if (data.models && data.models.length > 0) {
          setModels(data.models);
          setSelectedModel(data.models[0]);
        } else {
          setModels(FALLBACK_MODELS);
          setSelectedModel(FALLBACK_MODELS[0]);
        }
      } catch (error) {
        console.error('Failed to fetch models:', error);
        setModels(FALLBACK_MODELS);
        setSelectedModel(FALLBACK_MODELS[0]);
      } finally {
        setLoading(false);
      }
    };
    fetchModels();
  }, []);
  
  // Fetch dFans URL from backend
  useEffect(() => {
    fetch(`${API_URL}/api/lollipop/settings`)
      .then(res => res.json())
      .then(data => {
        if (data.dfans_url) {
          setDfansLink(data.dfans_url);
        }
      })
      .catch(err => console.log('Using default dFans URL'));
  }, []);

  // Auto-rotate hero images for selected model
  useEffect(() => {
    if (!selectedModel) return;
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % selectedModel.images.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [selectedModel]);

  // Reset image index when model changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [selectedModel]);

  // Get icon component from string name
  const getIcon = (iconName) => ICON_MAP[iconName] || Dumbbell;
  
  // Loading state
  if (loading || !selectedModel) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-slate-900 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-pink-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Loading the boys...</p>
        </div>
      </div>
    );
  }

  const SelectedIcon = getIcon(selectedModel.icon);

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-slate-900 to-black text-white">
      <Helmet>
        <title>My Boy Lollipop | Premium AI Male Models - Cash & Richo</title>
        <meta name="description" content="Exclusive AI-generated male model content. Cash the fitness model and Richo the island soul. Subscribe for exclusive content." />
      </Helmet>

      {/* Launch Promo Banner */}
      {showPromo && (
        <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 py-3 px-4 text-center relative">
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <Sparkles className="w-5 h-5 animate-pulse" />
            <span className="font-bold">LAUNCH SPECIAL:</span>
            <span>VIP Access - <span className="line-through opacity-70">$49.99</span> <span className="font-bold text-lg">$24.99/mo</span> (50% OFF!)</span>
            <Sparkles className="w-5 h-5 animate-pulse" />
          </div>
          <button 
            onClick={() => setShowPromo(false)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white"
          >
            ×
          </button>
        </div>
      )}

      {/* Model Selector Tabs */}
      <div className="bg-slate-900/80 backdrop-blur-sm border-b border-slate-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-center gap-2 py-3 overflow-x-auto">
            <span className="text-slate-400 text-sm mr-2 hidden sm:block">Choose Your Boy:</span>
            {models.map((model) => {
              const ModelIcon = getIcon(model.icon);
              return (
                <button
                  key={model.id}
                  onClick={() => setSelectedModel(model)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full font-semibold transition-all whitespace-nowrap ${
                    selectedModel.id === model.id
                      ? `bg-gradient-to-r ${model.gradient} text-white shadow-lg`
                      : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                  }`}
                >
                  <ModelIcon className="w-4 h-4" />
                  {model.name}
                </button>
              );
            })}
            {/* Admin Link */}
            <Link
              to="/lollipop/admin"
              className="flex items-center gap-2 px-3 py-2 rounded-full bg-slate-800/50 text-slate-500 hover:text-white hover:bg-slate-700 transition ml-2"
              title="Admin"
            >
              <Settings className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>

      {/* Hero Section - Dynamic Model */}
      <section className="relative overflow-hidden">
        {/* Animated Background - Color changes based on model */}
        <div className="absolute inset-0 opacity-30">
          <div className={`absolute top-20 left-10 w-72 h-72 rounded-full filter blur-[100px] animate-pulse ${
            selectedModel.id === 'cash' ? 'bg-pink-500' : 
            selectedModel.id === 'richo' ? 'bg-emerald-500' : 
            selectedModel.id === 'trey' ? 'bg-amber-500' : 
            selectedModel.id === 'boyz-to-men' ? 'bg-cyan-500' : 
            selectedModel.id === 'billy' ? 'bg-blue-500' : 
            selectedModel.id === 'garth' ? 'bg-violet-500' : 
            selectedModel.id === 'anton' ? 'bg-lime-500' : 'bg-rose-500'
          }`}></div>
          <div className={`absolute bottom-20 right-10 w-96 h-96 rounded-full filter blur-[120px] animate-pulse ${
            selectedModel.id === 'cash' ? 'bg-orange-500' : 
            selectedModel.id === 'richo' ? 'bg-teal-500' : 
            selectedModel.id === 'trey' ? 'bg-yellow-600' : 
            selectedModel.id === 'boyz-to-men' ? 'bg-sky-500' : 
            selectedModel.id === 'billy' ? 'bg-indigo-500' : 
            selectedModel.id === 'garth' ? 'bg-purple-500' : 
            selectedModel.id === 'anton' ? 'bg-green-500' : 'bg-pink-500'
          }`} style={{animationDelay: '1s'}}></div>
          <div className={`absolute top-1/2 left-1/2 w-64 h-64 rounded-full filter blur-[80px] animate-pulse ${
            selectedModel.id === 'cash' ? 'bg-red-500' : 
            selectedModel.id === 'richo' ? 'bg-green-500' : 
            selectedModel.id === 'trey' ? 'bg-orange-600' : 
            selectedModel.id === 'boyz-to-men' ? 'bg-blue-500' : 
            selectedModel.id === 'billy' ? 'bg-violet-500' : 
            selectedModel.id === 'garth' ? 'bg-fuchsia-500' : 
            selectedModel.id === 'anton' ? 'bg-emerald-500' : 'bg-fuchsia-500'
          }`} style={{animationDelay: '2s'}}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Text Content */}
            <div className="text-center lg:text-left order-2 lg:order-1">
              {/* Logo/Title */}
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black mb-4 tracking-tight">
                <span className="bg-gradient-to-r from-pink-400 via-orange-400 to-red-400 bg-clip-text text-transparent">
                  My Boy
                </span>
                <br />
                <span className="bg-gradient-to-r from-amber-300 via-yellow-300 to-orange-300 bg-clip-text text-transparent">
                  Lollipop
                </span>
              </h1>
              
              {/* Dynamic Model Introduction */}
              <div className="mb-6">
                <div className={`inline-flex items-center gap-2 bg-gradient-to-r ${selectedModel.gradient}/20 backdrop-blur-sm rounded-full px-4 py-2 mb-4`}>
                  <SelectedIcon className={`w-5 h-5 ${
                    selectedModel.id === 'cash' ? 'text-orange-400' : 
                    selectedModel.id === 'richo' ? 'text-emerald-400' : 
                    selectedModel.id === 'trey' ? 'text-amber-400' : 
                    selectedModel.id === 'boyz-to-men' ? 'text-cyan-400' : 
                    selectedModel.id === 'billy' ? 'text-blue-400' : 
                    selectedModel.id === 'garth' ? 'text-violet-400' : 
                    selectedModel.id === 'anton' ? 'text-lime-400' : 'text-pink-400'
                  }`} />
                  <span className={`font-semibold ${
                    selectedModel.id === 'cash' ? 'text-orange-300' : 
                    selectedModel.id === 'richo' ? 'text-emerald-300' : 
                    selectedModel.id === 'trey' ? 'text-amber-300' : 
                    selectedModel.id === 'boyz-to-men' ? 'text-cyan-300' : 
                    selectedModel.id === 'billy' ? 'text-blue-300' : 
                    selectedModel.id === 'garth' ? 'text-violet-300' : 
                    selectedModel.id === 'anton' ? 'text-lime-300' : 'text-pink-300'
                  }`}>Featuring</span>
                </div>
                <h2 className="text-4xl sm:text-5xl font-black text-white mb-2">
                  {selectedModel.name.toUpperCase()}
                </h2>
                <p className="text-xl text-slate-300">
                  {selectedModel.type} • {selectedModel.tagline}
                </p>
              </div>
              
              <p className="text-lg text-slate-400 mb-8 max-w-xl mx-auto lg:mx-0">
                Get exclusive access to {selectedModel.name}'s content, behind-the-scenes moments, and so much more. 
                <span className="text-pink-400"> Sweet, Spicy & Everything Nice.</span>
              </p>

              {/* CTA Button */}
              <a
                href={dfansLink}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-3 bg-gradient-to-r ${selectedModel.gradient} hover:opacity-90 text-white font-bold text-xl px-10 py-5 rounded-full shadow-2xl transform hover:scale-105 transition-all duration-300`}
              >
                <Play className="w-6 h-6" />
                Subscribe to {selectedModel.name}
                <ChevronRight className="w-6 h-6" />
              </a>

              <p className="mt-4 text-slate-500 text-sm">
                Join thousands of satisfied subscribers
              </p>
            </div>

            {/* Right - Hero Image */}
            <div className="order-1 lg:order-2 flex justify-center">
              <div className="relative">
                {/* Glow Effect - Dynamic color */}
                <div className={`absolute -inset-4 bg-gradient-to-r ${selectedModel.gradient} rounded-3xl blur-2xl opacity-40 animate-pulse`}></div>
                
                {/* Main Image */}
                <div className="relative rounded-3xl overflow-hidden shadow-2xl border-2 border-white/10">
                  <img 
                    src={selectedModel.images[currentImageIndex]}
                    alt={`${selectedModel.name} - ${selectedModel.type}`}
                    className="w-80 h-96 sm:w-96 sm:h-[500px] object-cover transition-all duration-700"
                  />
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  
                  {/* Name Badge */}
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="bg-black/60 backdrop-blur-sm rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-2xl font-black text-white">{selectedModel.name.toUpperCase()}</h3>
                          <p className={`text-sm ${
                            selectedModel.id === 'cash' ? 'text-orange-400' : 
                            selectedModel.id === 'richo' ? 'text-emerald-400' : 
                            selectedModel.id === 'trey' ? 'text-amber-400' : 
                            selectedModel.id === 'boyz-to-men' ? 'text-cyan-400' : 
                            selectedModel.id === 'billy' ? 'text-blue-400' : 
                            selectedModel.id === 'garth' ? 'text-violet-400' : 
                            selectedModel.id === 'anton' ? 'text-lime-400' : 'text-pink-400'
                          }`}>{selectedModel.type}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                          <span className="text-white font-bold">4.9</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Image Indicators */}
                <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
                  {selectedModel.images.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        idx === currentImageIndex 
                          ? `w-6 ${
                            selectedModel.id === 'cash' ? 'bg-orange-500' : 
                            selectedModel.id === 'richo' ? 'bg-emerald-500' : 
                            selectedModel.id === 'trey' ? 'bg-amber-500' : 
                            selectedModel.id === 'boyz-to-men' ? 'bg-cyan-500' : 
                            selectedModel.id === 'billy' ? 'bg-blue-500' : 
                            selectedModel.id === 'garth' ? 'bg-violet-500' : 
                            selectedModel.id === 'anton' ? 'bg-lime-500' : 'bg-pink-500'
                          }` 
                          : 'bg-white/30'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Model Content Gallery */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">
              {selectedModel.name}'s <span className={
                selectedModel.id === 'cash' ? 'text-orange-400' : 
                selectedModel.id === 'richo' ? 'text-emerald-400' : 
                selectedModel.id === 'trey' ? 'text-amber-400' : 
                selectedModel.id === 'boyz-to-men' ? 'text-cyan-400' : 
                selectedModel.id === 'billy' ? 'text-blue-400' : 
                selectedModel.id === 'garth' ? 'text-violet-400' : 
                selectedModel.id === 'anton' ? 'text-lime-400' : 'text-pink-400'
              }>Content</span>
            </h2>
            <p className="text-slate-400 text-lg">
              {selectedModel.id === 'cash' ? 'Exclusive gym sessions and fitness content' : 
               selectedModel.id === 'richo' ? 'Natural vibes and island lifestyle content' :
               selectedModel.id === 'trey' ? 'Honky tonk nights and cowboy adventures' :
               selectedModel.id === 'boyz-to-men' ? 'Pool party vibes and speedo season' :
               selectedModel.id === 'billy' ? 'Beach vibes and ocean adventures' :
               selectedModel.id === 'garth' ? 'Shower fresh and boy next door content' :
               selectedModel.id === 'anton' ? 'Resort life and Mediterranean heat' :
               'Steamy couples content and intimate moments'}
            </p>
          </div>

          {/* Content Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {selectedModel.content.map((content, idx) => (
              <a
                key={content.id}
                href={dfansLink}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative aspect-[3/4] rounded-2xl overflow-hidden cursor-pointer transform hover:scale-105 transition-all duration-300"
              >
                {/* Image Background */}
                <img 
                  src={selectedModel.images[idx]}
                  alt={content.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent"></div>
                
                {/* Lock Icon */}
                <div className="absolute top-3 right-3 bg-black/50 backdrop-blur-sm rounded-full p-2">
                  <Lock className="w-4 h-4 text-white/70" />
                </div>

                {/* Content Info */}
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <div className={`text-xs font-medium mb-1 ${
                    selectedModel.id === 'cash' ? 'text-orange-400' : 
                    selectedModel.id === 'richo' ? 'text-emerald-400' : 
                    selectedModel.id === 'trey' ? 'text-amber-400' : 
                    selectedModel.id === 'boyz-to-men' ? 'text-cyan-400' : 
                    selectedModel.id === 'billy' ? 'text-blue-400' : 
                    selectedModel.id === 'garth' ? 'text-violet-400' : 
                    selectedModel.id === 'anton' ? 'text-lime-400' : 'text-pink-400'
                  }`}>{content.type}</div>
                  <h3 className="text-lg font-bold text-white">{content.title}</h3>
                  <p className="text-sm text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity">
                    Unlock to view
                  </p>
                </div>

                {/* Hover Effect */}
                <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity ${
                  selectedModel.id === 'cash' ? 'bg-pink-500/20' : 
                  selectedModel.id === 'richo' ? 'bg-emerald-500/20' : 
                  selectedModel.id === 'trey' ? 'bg-amber-500/20' : 
                  selectedModel.id === 'boyz-to-men' ? 'bg-cyan-500/20' : 
                  selectedModel.id === 'billy' ? 'bg-blue-500/20' : 
                  selectedModel.id === 'garth' ? 'bg-violet-500/20' : 
                  selectedModel.id === 'anton' ? 'bg-lime-500/20' : 'bg-rose-500/20'
                }`}></div>
              </a>
            ))}
          </div>

          <div className="text-center mt-10">
            <a
              href={dfansLink}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center gap-2 font-semibold text-lg ${
                selectedModel.id === 'cash' ? 'text-pink-400 hover:text-pink-300' : 
                selectedModel.id === 'richo' ? 'text-emerald-400 hover:text-emerald-300' : 
                selectedModel.id === 'trey' ? 'text-amber-400 hover:text-amber-300' :
                selectedModel.id === 'boyz-to-men' ? 'text-cyan-400 hover:text-cyan-300' :
                selectedModel.id === 'billy' ? 'text-blue-400 hover:text-blue-300' :
                selectedModel.id === 'garth' ? 'text-violet-400 hover:text-violet-300' :
                selectedModel.id === 'anton' ? 'text-lime-400 hover:text-lime-300' :
                'text-rose-400 hover:text-rose-300'
              }`}
            >
              Unlock All {selectedModel.name}'s Content <ChevronRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Meet All Models Section */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Meet <span className="text-orange-400">The Boys</span>
            </h2>
            <p className="text-slate-400 text-lg">{models.length} unique personalities. Endless content.</p>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-8 gap-3 max-w-7xl mx-auto">
            {models.map((model) => {
              const ModelIcon = getIcon(model.icon);
              return (
                <button
                  key={model.id}
                  onClick={() => {
                    setSelectedModel(model);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className={`group relative overflow-hidden rounded-3xl aspect-[4/5] transition-all duration-300 ${
                    selectedModel.id === model.id ? 'ring-4 ring-white/50' : ''
                  }`}
                >
                  <img 
                    src={model.images[0]}
                    alt={model.name}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className={`inline-flex items-center gap-2 bg-gradient-to-r ${model.gradient} rounded-full px-3 py-1 mb-3`}>
                      <ModelIcon className="w-4 h-4 text-white" />
                      <span className="text-white text-sm font-medium">{model.type}</span>
                    </div>
                    <h3 className="text-3xl font-black text-white mb-1">{model.name}</h3>
                    <p className="text-slate-300">{model.tagline}</p>
                  </div>
                  {selectedModel.id === model.id && (
                    <div className="absolute top-4 right-4 bg-white text-black text-xs font-bold px-3 py-1 rounded-full">
                      VIEWING
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-gradient-to-b from-slate-900/50 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">
              Subscribe to <span className={selectedModel.id === 'cash' ? 'text-orange-400' : 'text-emerald-400'}>{selectedModel.name}</span>
            </h2>
            <p className="text-slate-400 text-lg">Choose your level of access</p>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {TIERS.map((tier) => {
              const Icon = tier.icon;
              return (
                <div
                  key={tier.name}
                  className={`relative rounded-3xl p-8 ${
                    tier.popular || tier.launch
                      ? 'bg-gradient-to-b from-slate-800 to-slate-900 border-2 border-orange-500/50 shadow-2xl shadow-orange-500/20'
                      : 'bg-slate-800/50 border border-slate-700'
                  }`}
                >
                  {/* Badge */}
                  {tier.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-orange-500 to-red-500 text-white text-sm font-bold px-4 py-1 rounded-full">
                      MOST POPULAR
                    </div>
                  )}
                  {tier.launch && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-400 to-orange-500 text-black text-sm font-bold px-4 py-1 rounded-full animate-pulse">
                      50% OFF LAUNCH
                    </div>
                  )}

                  {/* Icon */}
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${tier.color} flex items-center justify-center mb-6 mx-auto`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>

                  {/* Tier Name */}
                  <h3 className="text-2xl font-bold text-center mb-2">{tier.name}</h3>

                  {/* Price */}
                  <div className="text-center mb-6">
                    {tier.originalPrice && (
                      <span className="text-slate-500 line-through text-lg mr-2">{tier.originalPrice}</span>
                    )}
                    <span className="text-4xl font-black bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">
                      {tier.price}
                    </span>
                    <span className="text-slate-400">{tier.period}</span>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-8">
                    {tier.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-slate-300">
                        <Star className="w-4 h-4 text-orange-400 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <a
                    href={dfansLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`block text-center py-4 rounded-xl font-bold transition-all duration-300 ${
                      tier.popular || tier.launch
                        ? `bg-gradient-to-r ${tier.color} text-white hover:shadow-lg hover:shadow-orange-500/30`
                        : 'bg-slate-700 text-white hover:bg-slate-600'
                    }`}
                  >
                    Get {tier.name}
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className={`absolute inset-0 bg-gradient-to-r ${
          selectedModel.id === 'cash' 
            ? 'from-pink-600/20 via-red-600/20 to-orange-600/20' 
            : selectedModel.id === 'richo'
            ? 'from-emerald-600/20 via-teal-600/20 to-green-600/20'
            : selectedModel.id === 'trey'
            ? 'from-amber-600/20 via-yellow-600/20 to-orange-600/20'
            : selectedModel.id === 'boyz-to-men'
            ? 'from-cyan-600/20 via-sky-600/20 to-blue-600/20'
            : selectedModel.id === 'billy'
            ? 'from-blue-600/20 via-indigo-600/20 to-violet-600/20'
            : selectedModel.id === 'garth'
            ? 'from-violet-600/20 via-purple-600/20 to-fuchsia-600/20'
            : selectedModel.id === 'anton'
            ? 'from-lime-600/20 via-green-600/20 to-emerald-600/20'
            : 'from-pink-600/20 via-rose-600/20 to-fuchsia-600/20'
        }`}></div>
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6">
            Ready to Meet <span className={
              selectedModel.id === 'cash' ? 'text-orange-400' : 
              selectedModel.id === 'richo' ? 'text-emerald-400' : 
              selectedModel.id === 'trey' ? 'text-amber-400' : 
              selectedModel.id === 'boyz-to-men' ? 'text-cyan-400' : 
              selectedModel.id === 'billy' ? 'text-blue-400' : 
              selectedModel.id === 'garth' ? 'text-violet-400' : 
              selectedModel.id === 'anton' ? 'text-lime-400' : 'text-pink-400'
            }>{selectedModel.name}</span>?
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Subscribe now for instant access to all of {selectedModel.name}'s exclusive content.
            <br />
            New content added weekly.
          </p>
          <a
            href={dfansLink}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-3 bg-gradient-to-r ${selectedModel.gradient} hover:opacity-90 text-white font-bold text-xl px-12 py-6 rounded-full shadow-2xl transform hover:scale-105 transition-all duration-300`}
          >
            <Heart className="w-6 h-6" />
            Subscribe to {selectedModel.name} on dFans
            <ChevronRight className="w-6 h-6" />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-2xl font-bold bg-gradient-to-r from-pink-400 to-orange-400 bg-clip-text text-transparent mb-2">
            My Boy Lollipop
          </p>
          <p className="text-slate-500 text-sm">
            All models are AI-generated. Must be 18+ to subscribe.
          </p>
          <p className="text-slate-600 text-xs mt-4">
            © {new Date().getFullYear()} My Boy Lollipop. A Lionheart Digital Production.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default MyBoyLollipop;
